import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  Divider,
  Box,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tab,
  Tabs,
  GlobalStyles,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import PrintIcon from '@mui/icons-material/Print';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import LabelObjectRenderer from '@/components/labelDesigner/LabelObjectRenderer';
import { CANVAS_SCALE } from '@/components/labelDesigner/LabelCanvas';
import { apiClient } from '@/api/axiosClient';
import { applySequenceValue } from '@/lib/autoGenerate';
import { peekSequenceValues, reserveSequenceValues } from '@/lib/sequenceApi';
import type { GrnDto, GrnLineDto } from '@/types/grn';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { LabelObject, LabelTemplate } from '@/types/labelTemplate';
import type { MaterialDto, SupplierDto } from '@/types/materials';
import type { PrintedLabelDto, PrintHistoryDto, SubmitPrintJobRequest } from '@/types/printing';

// Smaller than the label designer's own CANVAS_SCALE — this preview shows one tile per physical
// label (could be dozens under Auto mode), so a slightly more compact scale keeps the grid
// scannable instead of each tile matching the designer's full working size.
const PREVIEW_SCALE = CANVAS_SCALE * 0.7;

interface PrintJobGroup {
  id: string;
  templateId: string;
  templateName: string;
  printedDate: string;
  printer: string;
  labels: PrintedLabelDto[];
  totalQty: number;
}

export default function GrnLabelPrintPage() {
  const { t } = useTranslation('labelPrinting');
  const { formatDate, formatDateTime } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: config } = useProcessConfiguration();
  const { data: grns = [] } = useQuery({
    queryKey: ['grns'],
    queryFn: async () => (await apiClient.get<GrnDto[]>('/goods-receipt-notes')).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ['label-templates'],
    queryFn: async () => (await apiClient.get<LabelTemplate[]>('/label-templates')).data,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['print-history'],
    queryFn: async () => (await apiClient.get<PrintHistoryDto[]>('/print-history')).data,
  });

  // Defaults to "AfterQc" (the safer default — see docs/PROCESS_FLOWS.md) until the real config
  // loads, so the screen doesn't briefly offer printing against un-inspected stock.
  const timing = config?.grnLabelPrintTiming ?? 'AfterQc';
  const isBeforeQc = timing === 'BeforeQc';

  const [pageTab, setPageTab] = useState<'print' | 'reprint'>('print');

  const [grnId, setGrnId] = useState('');
  const [grnLineId, setGrnLineId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [printMode, setPrintMode] = useState<'auto' | 'manual'>('auto');
  const [manualRows, setManualRows] = useState<number[]>([]);
  const [printBatch, setPrintBatch] = useState<LabelObject[][] | null>(null);
  // Parallel to printBatch — labelQuantities[i] is the Qty this physical label covers, merged into
  // its own sampleData at print time (see the #label-print-area render loop below) so a {{Qty}}
  // token shows the right value per label instead of the same number on every one.
  const [printBatchQuantities, setPrintBatchQuantities] = useState<number[] | null>(null);
  // The template/base-token-data the current printBatch was built from — captured at print/reprint
  // time rather than read live from Tab 1's selectedTemplate/sampleData, since a Reprint (Tab 2)
  // prints a different GRN/line/template than whatever happens to be selected in Tab 1.
  const [printBatchTemplate, setPrintBatchTemplate] = useState<LabelTemplate | null>(null);
  const [printBatchSampleData, setPrintBatchSampleData] = useState<Record<string, string> | null>(null);

  const [reprintGrnId, setReprintGrnId] = useState('');
  const [reprintGrnLineId, setReprintGrnLineId] = useState('');

  // "After QC": a line is only eligible once its QC (if any) has passed — a line with no QcTask
  // never needed QC and is treated as already approved. "Before QC": every line is eligible
  // immediately, since printing is meant to happen ahead of inspection.
  const isLinePrintable = (line: GrnLineDto) => isBeforeQc || line.qcStatus === null || line.qcStatus === 'Passed';
  const maxQtyFor = (line: GrnLineDto) => (isBeforeQc ? line.receivedQty : line.acceptedQty);
  const remainingQtyFor = (line: GrnLineDto) => Math.max(0, maxQtyFor(line) - line.labelPrintedQty);

  // A line only needs to appear here while it's both QC-eligible for this mode AND still has
  // unprinted quantity left — once every label for it has been printed it drops off the list
  // entirely (not just disabled), and once every line on a GRN has dropped off, the GRN itself
  // drops out of the "Select GRN" list below.
  const isLineSelectable = useCallback(
    (line: GrnLineDto) => isLinePrintable(line) && remainingQtyFor(line) > 0,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBeforeQc],
  );

  const selectableGrns = useMemo(() => grns.filter((g) => g.lines.some(isLineSelectable)), [grns, isLineSelectable]);

  const selectedGrn = selectableGrns.find((g) => g.id === grnId);
  const grnLines = useMemo(() => (selectedGrn?.lines ?? []).filter(isLineSelectable), [selectedGrn, isLineSelectable]);

  const selectedLine = grnLines.find((l) => l.id === grnLineId);

  // Real data behind Material/Supplier label bindings (see client/src/lib/labelFields.ts) —
  // previously several of these fields were hardcoded to '' here regardless of what was actually
  // selected, so a label bound to e.g. {{HsnCode}} printed blank no matter what.
  const { data: selectedMaterial } = useQuery({
    queryKey: ['materials', selectedLine?.materialId],
    queryFn: async () => (await apiClient.get<MaterialDto>(`/materials/${selectedLine!.materialId}`)).data,
    enabled: Boolean(selectedLine?.materialId),
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });
  const selectedSupplier = suppliers.find((s) => s.id === selectedGrn?.supplierId);

  // All PrintedLabel rows ever recorded (capped server-side) — used to work out which GRNs/lines
  // actually have print history, so the Reprint tab only ever offers something reprintable. The
  // per-line job list further down re-filters this same set down to the selected line.
  const { data: allPrintedLabels = [] } = useQuery({
    queryKey: ['printed-labels'],
    queryFn: async () => (await apiClient.get<PrintedLabelDto[]>('/printed-labels')).data,
  });
  const printedGrnLineIds = useMemo(() => new Set(allPrintedLabels.map((l) => l.grnLineId)), [allPrintedLabels]);

  // Reprint tab has its own GRN/line selection, filtered to only what actually has print history —
  // unlike Tab 1's grnLines, remaining qty (even zero) doesn't matter here, only "was this ever
  // printed."
  const reprintableGrns = useMemo(() => grns.filter((g) => g.lines.some((l) => printedGrnLineIds.has(l.id))), [grns, printedGrnLineIds]);
  const reprintGrn = reprintableGrns.find((g) => g.id === reprintGrnId);
  const reprintGrnLines = useMemo(() => (reprintGrn?.lines ?? []).filter((l) => printedGrnLineIds.has(l.id)), [reprintGrn, printedGrnLineIds]);
  const reprintLine = reprintGrnLines.find((l) => l.id === reprintGrnLineId);

  const { data: reprintMaterial } = useQuery({
    queryKey: ['materials', reprintLine?.materialId],
    queryFn: async () => (await apiClient.get<MaterialDto>(`/materials/${reprintLine!.materialId}`)).data,
    enabled: Boolean(reprintLine?.materialId),
  });
  const reprintSupplier = suppliers.find((s) => s.id === reprintGrn?.supplierId);

  const printedLabels = useMemo(() => allPrintedLabels.filter((l) => l.grnLineId === reprintGrnLineId), [allPrintedLabels, reprintGrnLineId]);

  const maxQty = selectedLine ? maxQtyFor(selectedLine) : 0;
  const remainingQty = selectedLine ? remainingQtyFor(selectedLine) : 0;

  // Splits remainingQty into Material.stuffingQty-sized chunks — e.g. 56 units at a stuffing qty
  // of 10 becomes [10,10,10,10,10,6] (5 full labels, the last covering the remainder). No Stuffing
  // Qty set (null/0) or a Stuffing Qty of 1 both mean "one unit per label" — 100 units becomes 100
  // labels of qty 1 each, not one label for the whole quantity.
  const autoBreakdown = useMemo(() => {
    if (remainingQty <= 0) return [];
    const rawStuffingQty = selectedMaterial?.stuffingQty;
    const stuffingQty = rawStuffingQty && rawStuffingQty > 0 ? rawStuffingQty : 1;

    const fullLabels = Math.floor(remainingQty / stuffingQty);
    const remainder = Math.round((remainingQty - fullLabels * stuffingQty) * 1000) / 1000;
    const breakdown = Array.from({ length: fullLabels }, () => stuffingQty);
    if (remainder > 0) breakdown.push(remainder);
    return breakdown;
  }, [remainingQty, selectedMaterial]);

  const labelQuantities = printMode === 'auto' ? autoBreakdown : manualRows;
  const manualTotal = manualRows.reduce((sum, q) => sum + q, 0);
  const manualValid = manualRows.length > 0 && manualRows.every((q) => q > 0) && manualTotal === remainingQty;

  // Reactively drops the current selection the moment it falls out of the eligible lists above —
  // e.g. right after a print completes and its line's remaining qty hits zero, or the GRN's last
  // remaining line is completed by someone else and the shared 'grns' query refetches.
  useEffect(() => {
    if (grnId && !selectableGrns.some((g) => g.id === grnId)) {
      setGrnId('');
    }
  }, [selectableGrns, grnId]);

  useEffect(() => {
    if (grnLineId && !grnLines.some((l) => l.id === grnLineId)) {
      setGrnLineId('');
    }
  }, [grnLines, grnLineId]);

  useEffect(() => {
    setGrnLineId('');
    setTemplateId('');
  }, [grnId]);

  useEffect(() => {
    setReprintGrnLineId('');
  }, [reprintGrnId]);

  useEffect(() => {
    if (!selectedLine) return;
    const remaining = remainingQtyFor(selectedLine);
    // Manual mode always starts as one row for the whole remaining quantity — the operator splits
    // it from there (see the "Split / Add Label" button below).
    setManualRows(remaining > 0 ? [remaining] : []);
    const activeTemplates = templates.filter((tpl) => tpl.status === 'Active' && tpl.assignmentType !== 'Packing' && tpl.assignmentType !== 'WeightProduction');
    const preferred = activeTemplates.find((tpl) => tpl.materialId === selectedLine.materialId) ?? activeTemplates.find((tpl) => !tpl.materialId);
    setTemplateId(preferred?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnLineId]);

  // Packing-assigned templates are excluded here — they're only offered on the Packing Label
  // Print screen, even if Active, so a layout built for package labels never leaks into GRN.
  const availableTemplates = useMemo(
    () =>
      templates
        .filter((tpl) => tpl.status === 'Active' && tpl.assignmentType !== 'Packing' && tpl.assignmentType !== 'WeightProduction')
        .filter((tpl) => (tpl.materialId ? tpl.materialId === selectedLine?.materialId : true)),
    [templates, selectedLine],
  );
  const selectedTemplate = templates.find((tpl) => tpl.id === templateId);

  // Shared between Tab 1's live preview and Tab 2's reprint (which prints a different GRN/line
  // than whatever Tab 1 currently has selected) — everything except Qty, which varies per physical
  // label and gets merged in separately (see the #label-print-area render loop below).
  const buildBaseSampleData = useCallback(
    (line: GrnLineDto | undefined, grn: GrnDto | undefined, material: MaterialDto | undefined, supplier: SupplierDto | undefined) => ({
      MaterialCode: line?.materialCode || 'SKU-00000',
      MaterialName: line?.materialName || t('sampleMaterial'),
      CategoryName: material?.categoryName ?? '',
      UomCode: material?.uomCode ?? '',
      Barcode: material?.barcode ?? '',
      HsnCode: material?.hsnCode ?? '',
      GstRate: material?.gstRate != null ? String(material.gstRate) : '',
      ManufacturerName: material?.manufacturerName ?? '',
      AbcClass: material?.abcClass ?? '',
      SupplierCode: supplier?.code ?? '',
      SupplierName: grn?.supplierName || supplier?.name || '',
      SupplierGstin: supplier?.gstin ?? '',
      SupplierAddress: supplier?.address ?? '',
      SupplierContactPerson: supplier?.contactPerson ?? '',
      SupplierPhone: supplier?.phone ?? '',
      GrnNumber: grn?.grnNumber ?? '',
      PurchaseOrderNumber: grn?.purchaseOrderNumber ?? '',
      WarehouseName: grn?.warehouseName ?? '',
      GrnDate: grn?.grnDate ? formatDate(grn.grnDate) : '',
      VehicleNo: grn?.vehicleNo ?? '',
      InwardDocNo: grn?.inwardDocNo ?? '',
      Status: grn?.status ?? '',
      // Blank on a non-3PL GRN (clientName is only ever set once 3PL is enabled) — same
      // "resolves to empty string when not applicable" pattern as every other optional field here.
      ClientName: grn?.clientName ?? '',
      BatchNo: line?.batchNo ?? '',
      MfgDate: line?.mfgDate ? formatDate(line.mfgDate) : '',
      ExpiryDate: line?.expiryDate ? formatDate(line.expiryDate) : '',
      OrderedQty: line ? String(line.orderedQty) : '',
      ReceivedQty: line ? String(line.receivedQty) : '',
      AcceptedQty: line ? String(line.acceptedQty) : '',
      RejectedQty: line ? String(line.rejectedQty) : '',
      UnitPrice: line ? String(line.unitPrice) : '',
      PrintDate: dayjs().format('MMM D, YYYY'),
      PrintTime: dayjs().format('HH:mm'),
    }),
    [t, formatDate],
  );

  const sampleData = useMemo(
    () => ({
      ...buildBaseSampleData(selectedLine, selectedGrn, selectedMaterial, selectedSupplier),
      // Qty shows the FIRST label's quantity here (the shared canvas preview above the print
      // button can only show one instance); the actual per-label value is merged in for each
      // physical label individually in the #label-print-area render loop below.
      Qty: labelQuantities[0] != null ? String(labelQuantities[0]) : '',
    }),
    [buildBaseSampleData, selectedLine, selectedGrn, selectedMaterial, selectedSupplier, labelQuantities],
  );

  // Every auto-generating object gets its own run of values, so each preview tile can show the
  // barcode that tile will actually carry. Without this the same sampleData rendered into all of
  // them and every label looked identical. Peeked, never reserved — previewing must not burn
  // barcode numbers, so printing still reserves for real (see printMutation).
  const autoGenObjects = useMemo(
    () => (selectedTemplate?.objects ?? []).filter(
      (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> =>
        (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
    ),
    [selectedTemplate],
  );

  const { data: previewValues } = useQuery({
    queryKey: ['label-sequence-peek', templateId, labelQuantities.length, autoGenObjects.map((o) => o.id).join(',')],
    queryFn: async () => {
      const map = new Map<string, string[]>();
      for (const obj of autoGenObjects) {
        map.set(obj.id, await peekSequenceValues(templateId, obj.id, obj.autoGenerate!, labelQuantities.length));
      }
      return map;
    },
    enabled: Boolean(templateId) && autoGenObjects.length > 0 && labelQuantities.length > 0,
  });

  const totalLabelQty = labelQuantities.reduce((sum, q) => sum + q, 0);

  const readyToPrint = Boolean(
    grnLineId &&
      templateId &&
      selectedLine &&
      labelQuantities.length > 0 &&
      totalLabelQty > 0 &&
      totalLabelQty <= remainingQty &&
      (printMode === 'auto' || manualValid),
  );

  useEffect(() => {
    if (!printBatch) return;
    const handleAfterPrint = () => setPrintBatch(null);
    window.addEventListener('afterprint', handleAfterPrint);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
      cancelAnimationFrame(raf);
    };
  }, [printBatch]);

  const printMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLine || !selectedTemplate) throw new Error('Select a GRN item and template first.');

      // Deliberately the same list the preview renders from — if these two ever diverged, the
      // labels that print would not be the ones the operator approved on screen.
      const labelCount = labelQuantities.length;
      let barcodeValues: string[] | undefined;

      if (autoGenObjects.length === 0) {
        setPrintBatch(Array.from({ length: labelCount }, () => selectedTemplate.objects));
      } else {
        const valuesByObjectId = new Map<string, string[]>();
        for (const obj of autoGenObjects) {
          valuesByObjectId.set(obj.id, await reserveSequenceValues(selectedTemplate.id, obj.id, obj.autoGenerate!, labelCount));
        }
        const instances = Array.from({ length: labelCount }, (_, i) =>
          selectedTemplate.objects.map((o) => {
            if (!valuesByObjectId.has(o.id)) return o;
            const value = valuesByObjectId.get(o.id)![i];
            return applySequenceValue(o, value);
          }),
        );
        setPrintBatch(instances);

        // The canonical value persisted per unit for per-barcode QC — prefer an actual barcode
        // object, fall back to QR, then to whichever auto-generating field the template has.
        const barcodeObject = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];
        barcodeValues = valuesByObjectId.get(barcodeObject.id);
      }

      setPrintBatchQuantities(labelQuantities);
      setPrintBatchTemplate(selectedTemplate);
      setPrintBatchSampleData(sampleData);

      // No printer/station is collected up front any more — Print triggers the browser's native
      // print dialog (see the printBatch useEffect below), where the operator picks the
      // destination printer themselves.
      const payload: SubmitPrintJobRequest = { grnLineId: selectedLine.id, labelTemplateId: selectedTemplate.id, labelQuantities, printer: '', barcodeValues };
      await apiClient.post('/print-jobs', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-history'] });
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      queryClient.invalidateQueries({ queryKey: ['printed-labels'] });
    },
  });

  // Groups the GRN line's accumulated PrintedLabel rows back into "print jobs" by
  // printHistoryEntryId — this is the "GRN and GRN line item wise" accumulation the Reprint tab
  // shows, one row per job actually printed (not per physical label).
  const reprintJobGroups = useMemo<PrintJobGroup[]>(() => {
    const map = new Map<string, PrintJobGroup>();
    for (const label of printedLabels) {
      const existing = map.get(label.printHistoryEntryId);
      if (existing) {
        existing.labels.push(label);
        existing.totalQty += label.qty;
      } else {
        map.set(label.printHistoryEntryId, {
          id: label.printHistoryEntryId,
          templateId: label.labelTemplateId,
          templateName: label.templateName,
          printedDate: label.printedDate,
          printer: label.printer,
          labels: [label],
          totalQty: label.qty,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.printedDate < b.printedDate ? 1 : -1));
  }, [printedLabels]);

  const reprintMutation = useMutation({
    mutationFn: async (group: PrintJobGroup) => {
      const template = templates.find((tpl) => tpl.id === group.templateId);
      if (!template) throw new Error('The original label template no longer exists.');

      // Re-uses each label's originally recorded BarcodeValue/Qty verbatim — deliberately never
      // calls reserveSequenceValues here, since a reprint must not advance or duplicate a barcode
      // counter (per request: reprint should use "already printed data only").
      const autoGenObjects = template.objects.filter(
        (o): o is Extract<LabelObject, { type: 'barcode' | 'qr' | 'text' }> => (o.type === 'barcode' || o.type === 'qr' || o.type === 'text') && !!o.autoGenerate,
      );
      const primary = autoGenObjects.find((o) => o.type === 'barcode') ?? autoGenObjects.find((o) => o.type === 'qr') ?? autoGenObjects[0];

      const instances = group.labels.map((label) =>
        template.objects.map((o) => {
          if (!primary || o.id !== primary.id || !label.barcodeValue) return o;
          return applySequenceValue(o, label.barcodeValue);
        }),
      );

      setPrintBatch(instances);
      setPrintBatchQuantities(group.labels.map((l) => l.qty));
      setPrintBatchTemplate(template);
      setPrintBatchSampleData(buildBaseSampleData(reprintLine, reprintGrn, reprintMaterial, reprintSupplier));

      await apiClient.post(`/print-history/${group.id}/reprint`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-history'] });
    },
  });

  const historyColumns: DataTableColumn<PrintHistoryDto>[] = [
    { field: 'dateTime', headerName: t('history.date'), render: (r) => formatDateTime(r.dateTime) },
    { field: 'grnNumber', headerName: t('history.grn'), render: (r) => r.grnNumber ?? '—' },
    { field: 'material', headerName: t('history.material'), render: (r) => `${r.materialCode} — ${r.material}` },
    { field: 'template', headerName: t('history.template'), filterable: true },
    { field: 'quantity', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer'), filterable: true },
    { field: 'user', headerName: t('history.user'), filterable: true },
  ];

  const reprintColumns: DataTableColumn<PrintJobGroup>[] = [
    { field: 'printedDate', headerName: t('history.date'), render: (r) => formatDateTime(r.printedDate) },
    { field: 'templateName', headerName: t('history.template') },
    { field: 'labelCount', headerName: t('reprint.labelCount'), render: (r) => r.labels.length },
    { field: 'totalQty', headerName: t('history.quantity') },
    { field: 'printer', headerName: t('history.printer') },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Button size="small" variant="outlined" startIcon={<PrintIcon fontSize="small" />} onClick={() => reprintMutation.mutate(r)} disabled={reprintMutation.isPending}>
          {t('reprint.reprintButton')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <GlobalStyles
        styles={{
          '@media print': {
            'body *': { visibility: 'hidden' },
            '#label-print-area, #label-print-area *': { visibility: 'visible' },
            // position: absolute (not fixed) — fixed pins the element to the viewport on every
            // printed page, which makes Chrome clip/repeat its content per page instead of letting
            // it flow across pages, so all N labels' combined height got squeezed into however many
            // sheets that total height spanned (e.g. 100 labels -> 4 pages) instead of one page per
            // label. Absolute keeps the element out of the (hidden) body's flow without that
            // per-page clipping, so each child's page-break-after below is honored individually.
            '#label-print-area': { position: 'absolute', top: 0, left: 0, margin: 0 },
          },
          // Requests the label's own design size as the physical page size — on a thermal/label
          // printer loaded with label-sized stock (matching what's set up in the OS printer's
          // media settings), the browser's print dialog then paginates at that exact size instead
          // of a default sheet size like A4, so one page truly is one label.
          '@page': {
            size: printBatchTemplate ? `${printBatchTemplate.widthMm}mm ${printBatchTemplate.heightMm}mm` : 'auto',
            margin: 0,
          },
        }}
      />

      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <Tabs value={pageTab} onChange={(_, v: 'print' | 'reprint') => setPageTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="print" label={t('tabs.print')} />
        <Tab value="reprint" label={t('tabs.reprint')} />
      </Tabs>

      {pageTab === 'print' && (
        <>
          <Alert severity={isBeforeQc ? 'warning' : 'info'} sx={{ mb: 3 }}>
            {isBeforeQc ? t('modeBeforeQc') : t('modeAfterQc')}
          </Alert>

          <GridContainer columns={12}>
            <GridItem xs={12} md={4}>
              <Stack spacing={3}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <TuneIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('selectionCriteria')}</Typography>
                    </Stack>
                    <Stack spacing={2}>
                      <SearchableSelect
                        label={t('selectGrn')} fullWidth value={grnId} onChange={setGrnId}
                        options={selectableGrns.map((g) => ({ value: g.id, label: `${g.grnNumber} — ${g.supplierName}` }))}
                      />
                      <SearchableSelect
                        label={t('selectItem')}
                        fullWidth
                        value={grnLineId}
                        onChange={setGrnLineId}
                        disabled={!grnId}
                        helperText={grnId && grnLines.length === 0 ? t('noItemsOnGrn') : undefined}
                        options={grnLines.map((line) => ({ value: line.id, label: `${line.materialCode} — ${line.materialName}` }))}
                      />
                      <SearchableSelect
                        label={t('selectTemplate')}
                        fullWidth
                        value={templateId}
                        onChange={setTemplateId}
                        disabled={!grnLineId}
                        helperText={grnLineId && availableTemplates.length === 0 ? t('noTemplatesAvailable') : undefined}
                        options={availableTemplates.map((tpl) => ({ value: tpl.id, label: tpl.name }))}
                      />
                    </Stack>
                  </CardContent>
                </Card>

                {selectedLine && (
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <InfoOutlinedIcon fontSize="small" color="primary" />
                          <Typography variant="h5">{t('itemDetails')}</Typography>
                        </Stack>
                        {selectedLine.qcStatus && <StatusChip status={selectedLine.qcStatus} />}
                      </Stack>
                      <GridContainer columns={12} spacing={2}>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('itemFields.batchNo')}
                          </Typography>
                          <Typography variant="body2">{selectedLine.batchNo ?? '—'}</Typography>
                        </GridItem>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {isBeforeQc ? t('itemFields.receivedQty') : t('itemFields.acceptedQty')}
                          </Typography>
                          <Typography variant="body2">{maxQty}</Typography>
                        </GridItem>
                        <GridItem xs={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                            {t('itemFields.labelPrintedQty')}
                          </Typography>
                          <Typography variant="body2">{selectedLine.labelPrintedQty}</Typography>
                        </GridItem>
                      </GridContainer>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <PrintIcon fontSize="small" color="primary" />
                      <Typography variant="h5">{t('configuration')}</Typography>
                    </Stack>

                    <ToggleButtonGroup
                      color="primary"
                      exclusive
                      fullWidth
                      size="small"
                      value={printMode}
                      onChange={(_, value: 'auto' | 'manual' | null) => value && setPrintMode(value)}
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="auto">{t('mode.auto')}</ToggleButton>
                      <ToggleButton value="manual">{t('mode.manual')}</ToggleButton>
                    </ToggleButtonGroup>

                    {printMode === 'auto' ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {selectedMaterial?.stuffingQty && selectedMaterial.stuffingQty > 1
                            ? t('mode.autoHint', { total: remainingQty, stuffingQty: selectedMaterial.stuffingQty })
                            : t('mode.autoNoStuffingQty', { total: remainingQty })}
                        </Typography>
                        {autoBreakdown.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {autoBreakdown.map((q, i) => (
                              <Chip key={i} size="small" label={`${t('mode.labelN', { n: i + 1 })}: ${q}`} />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ mb: 2 }}>
                        <Stack spacing={1}>
                          {manualRows.map((qty, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                              <TextField
                                size="small"
                                type="number"
                                label={t('mode.labelN', { n: i + 1 })}
                                fullWidth
                                value={qty}
                                onChange={(e) => {
                                  const value = Number(e.target.value);
                                  setManualRows((rows) => rows.map((r, idx) => (idx === i ? value : r)));
                                }}
                              />
                              <IconButton
                                size="small"
                                disabled={manualRows.length <= 1}
                                onClick={() => setManualRows((rows) => rows.filter((_, idx) => idx !== i))}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setManualRows((rows) => [...rows, Math.max(0, remainingQty - rows.reduce((sum, q) => sum + q, 0))])}
                          sx={{ mt: 1 }}
                        >
                          {t('mode.addLabel')}
                        </Button>
                        <Typography variant="caption" display="block" color={manualValid ? 'text.secondary' : 'error'} sx={{ mt: 1 }}>
                          {t('mode.allocatedOf', { allocated: manualTotal, total: remainingQty })}
                          {!manualValid && ` — ${t('mode.mismatchHint', { total: remainingQty })}`}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 2.5 }} />
                    <Button fullWidth size="large" variant="contained" startIcon={<PrintIcon />} onClick={() => printMutation.mutate()} disabled={printMutation.isPending || !readyToPrint}>
                      {printMutation.isPending ? t('sendingToPrinter') : t('printLabels', { count: labelQuantities.length || 0 })}
                    </Button>
                  </CardContent>
                </Card>
              </Stack>
            </GridItem>

            <GridItem xs={12} md={8}>
              <Card sx={{ height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
                    {t('canvasPreviewTitle')}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {selectedTemplate && <Chip size="small" label={`${selectedTemplate.widthMm}×${selectedTemplate.heightMm} mm`} />}
                    {selectedTemplate && labelQuantities.length > 0 && (
                      <Chip size="small" color="primary" variant="filled" label={t('printLabels', { count: labelQuantities.length })} />
                    )}
                  </Stack>
                </Stack>
                <Box sx={{ minHeight: 480, maxHeight: 640, overflow: 'auto', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 2, bgcolor: 'action.hover', p: 3 }}>
                  {!selectedTemplate ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('selectTemplatePrompt')}
                      </Typography>
                    </Box>
                  ) : labelQuantities.length === 0 ? (
                    <Box sx={{ width: '100%', minHeight: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('noLabelsToPreview')}
                      </Typography>
                    </Box>
                  ) : (
                    // One tile per physical label about to print, each with its own Qty token —
                    // this is the "based on label qty, preview should display no of labels" view.
                    labelQuantities.map((qty, i) => (
                      <Stack key={i} spacing={0.5} alignItems="center">
                        <Box
                          sx={{
                            position: 'relative',
                            width: selectedTemplate.widthMm * PREVIEW_SCALE,
                            height: selectedTemplate.heightMm * PREVIEW_SCALE,
                            bgcolor: '#fff',
                            boxShadow: '0px 4px 14px rgba(0,0,0,0.15)',
                          }}
                        >
                          {selectedTemplate.objects.map((obj) => {
                            // Same substitution the print path performs when it builds each
                            // instance — kept identical so what is previewed is what prints.
                            const values = previewValues?.get(obj.id);
                            const instance = values?.[i] != null ? applySequenceValue(obj, values[i]) : obj;
                            return (
                              <LabelObjectRenderer key={obj.id} object={instance} sampleData={{ ...sampleData, Qty: String(qty) }} scale={PREVIEW_SCALE} />
                            );
                          })}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('mode.labelN', { n: i + 1 })}
                        </Typography>
                      </Stack>
                    ))
                  )}
                </Box>
              </Card>
            </GridItem>
          </GridContainer>

          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <HistoryIcon fontSize="small" color="primary" />
              <Typography variant="h5">{t('history.title')}</Typography>
            </Stack>
            <DataTable columns={historyColumns} rows={history} searchable exportFileName="print-history" />
          </Box>
        </>
      )}

      {pageTab === 'reprint' && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <TuneIcon fontSize="small" color="primary" />
                <Typography variant="h5">{t('reprint.selectionCriteria')}</Typography>
              </Stack>
              <GridContainer columns={12} spacing={2}>
                <GridItem xs={12} md={6}>
                  <SearchableSelect
                    label={t('selectGrn')}
                    fullWidth
                    value={reprintGrnId}
                    onChange={setReprintGrnId}
                    helperText={reprintableGrns.length === 0 ? t('reprint.noPrintedGrns') : undefined}
                    options={reprintableGrns.map((g) => ({ value: g.id, label: `${g.grnNumber} — ${g.supplierName}` }))}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <SearchableSelect
                    label={t('selectItem')}
                    fullWidth
                    value={reprintGrnLineId}
                    onChange={setReprintGrnLineId}
                    disabled={!reprintGrnId}
                    helperText={reprintGrnId && reprintGrnLines.length === 0 ? t('reprint.noPrintedItems') : undefined}
                    options={reprintGrnLines.map((line) => ({ value: line.id, label: `${line.materialCode} — ${line.materialName}` }))}
                  />
                </GridItem>
              </GridContainer>
            </CardContent>
          </Card>

          {reprintGrnLineId && (
            <DataTable columns={reprintColumns} rows={reprintJobGroups} searchable={false} exportable={false} emptyMessage={t('reprint.noHistory')} />
          )}
        </Stack>
      )}

      {/* Hidden except under @media print (see GlobalStyles above) — one label per physical copy,
          each a fresh page so the browser's print dialog paginates them naturally. Shared by both
          tabs: Tab 1's printMutation and Tab 2's reprintMutation both populate printBatch/
          printBatchTemplate/printBatchSampleData/printBatchQuantities before calling window.print(). */}
      <Box id="label-print-area" sx={{ display: 'none', '@media print': { display: 'block' } }}>
        {printBatch?.map((objects, i) => {
          const labelSampleData = { ...(printBatchSampleData ?? {}), Qty: String(printBatchQuantities?.[i] ?? '') };
          return (
            <Box
              key={i}
              sx={{
                position: 'relative',
                width: `${printBatchTemplate?.widthMm ?? 100}mm`,
                height: `${printBatchTemplate?.heightMm ?? 60}mm`,
                // Both properties set for browser compatibility (breakAfter is the modern
                // Fragmentation spec name, pageBreakAfter the legacy alias some engines still key
                // off) — without a hard break here, browsers may pack several small labels onto
                // one physical print page instead of the one-page-per-label count shown on screen.
                breakAfter: i === printBatch.length - 1 ? 'auto' : 'page',
                breakInside: 'avoid',
                pageBreakAfter: i === printBatch.length - 1 ? 'auto' : 'always',
                pageBreakInside: 'avoid',
              }}
            >
              {objects.map((obj) => (
                <LabelObjectRenderer key={obj.id} object={obj} sampleData={labelSampleData} scale={3.7795} />
              ))}
            </Box>
          );
        })}
      </Box>
    </>
  );
}
